import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../../App"; 
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, CreditCard, RefreshCw, Zap, Shield, Share2 } from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';

const ConnectBank = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);

  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch premium status function
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      fetchPremiumStatus(); // Fetch premium status
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);
      return () => clearInterval(interval);
    } else {
      navigate("/demo");
    }
  }, [user, navigate]);

  const navItems = [
    { icon: Home, label: t('connectBank.nav.home'), path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('connectBank.nav.notifications'),
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('connectBank.nav.marketplace'), path: "/marketplace" },
    { icon: Share2, label: t('connectBank.nav.shareSubs'), path: "/share-subs" },
    { icon: ShoppingBag, label: t('connectBank.nav.connectBank'), path: "/connectBank" },
    { icon: Users, label: t('connectBank.nav.partners'), path: "/partners" },
    { icon: Settings, label: t('connectBank.nav.settings'), path: "/account-settings" },
  ];

  const features = [
    {
      icon: CreditCard,
      title: t('connectBank.features.automaticTracking.title'),
      description: t('connectBank.features.automaticTracking.description')
    },
    {
      icon: RefreshCw,
      title: t('connectBank.features.realTimeUpdates.title'),
      description: t('connectBank.features.realTimeUpdates.description')
    },
    {
      icon: Zap,
      title: t('connectBank.features.quickActions.title'),
      description: t('connectBank.features.quickActions.description')
    },
    {
      icon: Shield,
      title: t('connectBank.features.secureConnection.title'),
      description: t('connectBank.features.secureConnection.description')
    }
  ];

  // Show loading if user is not authenticated
  if (!user) {
    return (
      <div className="flex h-screen bg-gray-50 justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
        
        <div className="p-6 space-y-6">
          {/* Updated with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg transition-all duration-500`}>
            <h1 className="text-2xl font-bold mb-2">{t('connectBank.title')}</h1>
            <p className="text-white/90">{t('connectBank.subtitle')}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="text-center py-8">
              <CreditCard className="h-16 w-16 mx-auto text-[#253985] mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-3">{t('connectBank.comingSoon.title')}</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('connectBank.comingSoon.description')}
              </p>
              
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <feature.icon className="h-8 w-8 text-[#253985] mb-3" />
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectBank;